import { useState, useRef } from 'react';
import { X } from 'lucide-react';
import { useStore } from '../store';
import { t } from '../i18n/translations';

interface Props {
  onClose: () => void;
}

export function BrandingSettingsModal({ onClose }: Props) {
  const { branding, updateBranding, uploadLogo, loadBranding, applyTheme, user } = useStore();
  const lang = user?.lang || 'de';

  const [appName, setAppName] = useState(branding?.app_name || '');
  const [primaryColor, setPrimaryColor] = useState(branding?.primary_color || '#646cff');
  const [accentColor, setAccentColor] = useState(branding?.accent_color || '#646cff');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(branding?.logo_url || null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (logoFile) {
        await uploadLogo(logoFile);
      }
      await updateBranding({ app_name: appName, primary_color: primaryColor, accent_color: accentColor });
      await loadBranding();
      applyTheme();
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        onClose();
      }, 800);
    } catch (err) {
      console.error('Branding save error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md bg-bg-surface border border-slate-800 rounded-2xl p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-main">{t('settings.branding_title', lang)}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-text-muted hover:text-text-main hover:bg-bg-elevated">
            <X size={18} />
          </button>
        </div>

        {/* App Name */}
        <div>
          <label className="block text-sm text-text-muted mb-1">{t('settings.app_name', lang)}</label>
          <input
            type="text"
            value={appName}
            onChange={(e) => setAppName(e.target.value)}
            className="w-full bg-bg-input border border-slate-800 rounded-lg px-3 py-2.5 text-text-main focus:outline-none focus:border-accent"
          />
        </div>

        {/* Logo */}
        <div>
          <label className="block text-sm text-text-muted mb-2">{t('settings.logo', lang)}</label>
          <div className="flex items-center gap-4">
            {logoPreview && (
              <img src={logoPreview} alt="Logo" className="h-12 w-12 object-contain rounded-lg bg-bg-elevated p-1" />
            )}
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="px-3 py-2 text-sm bg-bg-elevated border border-slate-700 rounded-lg text-text-muted hover:text-text-main transition-colors"
            >
              {t('settings.upload_logo', lang)}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        </div>

        {/* Primary Color */}
        <div>
          <label className="block text-sm text-text-muted mb-1">{t('settings.primary_color', lang)}</label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              className="h-9 w-16 rounded cursor-pointer border-0 bg-transparent"
            />
            <span className="text-sm text-text-muted font-mono">{primaryColor}</span>
          </div>
        </div>

        {/* Accent Color */}
        <div>
          <label className="block text-sm text-text-muted mb-1">{t('settings.accent_color', lang)}</label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={accentColor}
              onChange={(e) => setAccentColor(e.target.value)}
              className="h-9 w-16 rounded cursor-pointer border-0 bg-transparent"
            />
            <span className="text-sm text-text-muted font-mono">{accentColor}</span>
          </div>
        </div>

        {/* Save */}
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-2.5 bg-accent hover:bg-accent-hover rounded-lg text-white font-medium disabled:opacity-50 transition-colors"
        >
          {saved ? t('settings.saved', lang) : saving ? '...' : t('settings.save', lang)}
        </button>
      </div>
    </div>
  );
}
